<template>
    <vx-modal v-model:visible="visible" padding="standard" size="md">
        <template v-slot:header>New Reserved Date</template>

        <div class="flex flex-col gap-4">
            <vx-alert color="info" :closeable="false">
                <template v-slot:icon>
                    <font-awesome-icon :icon="faCalendarStar" />
                </template>

                <span>
                    New Reservation for
                    <strong>{{ dateFormatted }}</strong>
                </span>
            </vx-alert>

            <div>
                <vx-label>Which types should be reserved?</vx-label>

                <audience-type-selector
                    :marketing="formData.marketing"
                    :transactional="formData.transactional"
                    :error="displayFirstError(validation.type)"
                    @update:marketing="formData.marketing = $event"
                    @update:transactional="formData.transactional = $event"
                />
            </div>

            <div>
                <vx-label>
                    <div class="flex items-center gap-2">
                        <span>Reason</span>
                        <vx-badge color="violet" size="xs">
                            <span class="pb-[1.5px] text-xs leading-snug">Optional</span>
                            <font-awesome-icon widthAuto :icon="faPencilSlash" size="xs" />
                        </vx-badge>
                    </div>
                </vx-label>
                <vx-input
                    v-model="formData.reason"
                    size="lg"
                    :rows="4"
                    :maxlength="80"
                    :counter="true"
                    :error="displayFirstError(validation.reason)"
                />
            </div>
        </div>

        <template v-slot:footer>
            <div class="flex flex-col-reverse gap-4 justify-between lg:flex-row">
                <vx-button color="muted" size="lg" @click="visible = false">Cancel</vx-button>
                <vx-button
                    color="primary"
                    size="lg"
                    :loading="processing"
                    @click="submit()"
                    data-test="reserved-date-submit"
                    class="grow"
                >
                    Reserve Date
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>

<script lang="ts" setup>
import { faCalendarStar, faPencilSlash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxAlert, VxLabel, VxBadge, VxButton, VxInput, VxModal } from '@voxie/frontend-components';
import AudienceTypeSelector from '~/components/reserved-dates/AudienceTypeSelector.vue';
import { computed, ref, watch } from 'vue';
import { helpers, maxLength } from '@vuelidate/validators';
import { useFormSubmission } from '~/composables/useFormSubmission';
import { displayFirstError } from '~/utils/validation';
import reservedDatesClient from '~/services/reservedDates/reservedDatesClient';
import type { ReservedDate } from '~/services/reservedDates/reservedDatesClient.types';
import type { ClientContext } from '~/services/contextClient';
import dayjs from '~/utils/dayjs';

const props = defineProps<{
    date: string;
}>();

const visible = defineModel<boolean>('visible', { required: true });

const emit = defineEmits<{
    (e: 'created', reservedDate: ReservedDate): void;
}>();

const context: ClientContext = { teamId: Spark.state.currentTeam.id };

const defaultFormData = () => ({
    marketing: true,
    transactional: false,
    reason: '',
});

const formData = ref(defaultFormData());

const dateFormatted = computed(() => {
    if ('' === props.date) {
        return '';
    }
    const parsed = dayjs.utc(props.date);
    return parsed.isValid() ? parsed.format('MMMM Do, YYYY') : '';
});

const atLeastOneType = helpers.withMessage(
    'At least one audience type must be selected.',
    () => formData.value.marketing || formData.value.transactional
);

const { validation, submit, processing } = useFormSubmission({
    data: formData,
    validation: {
        type: { atLeastOneType },
        reason: { maxLength: maxLength(80) },
    },
    submit: async () => {
        const response = await reservedDatesClient.createReservedDate(context, {
            date: props.date,
            marketing: formData.value.marketing,
            transactional: formData.value.transactional,
            reason: '' === formData.value.reason ? null : formData.value.reason,
        });
        emit('created', response.data);
        visible.value = false;
        return response;
    },
    reset: () => {
        formData.value = defaultFormData();
    },
    preserveScroll: true,
    notification: () => ({ message: 'Reserved date created' }),
});

watch(visible, (newVal) => {
    if (!newVal) {
        formData.value = defaultFormData();
        validation.value.$reset();
    }
});
</script>
